import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import WarscrollGW, { AbilityCard } from '../components/WarscrollGW';
import VoiceControlWidget from '../components/VoiceControlWidget';
import { useVoiceCommands } from '../VoiceControlContext';

// Space reserved for a unit's thumbnail when it fits (image width + gap) —
// shared between the actual CSS layout and the layout-impact measurement
// below, so what gets measured matches what actually renders.
const UNIT_THUMB_RESERVED_PX = 76;

// Unit Abilities cards embed the unit's thumbnail on the right IF doing so
// doesn't push the card's own text past 1 extra wrapped line versus its
// current (full-width, no-thumbnail) layout — measured directly via
// scrollHeight rather than guessed from character counts, so it accounts for
// the card's real font/line-height/existing bullets. Runs once per card via
// a synchronous layout-effect measure/revert (no visible flicker: the
// thumbnail starts absent and only appears once we know it fits).
function UnitAbilityCard({ ab, hasImage }) {
  const cardWrapRef = useRef(null);
  const [fits, setFits] = useState(false);
  // Some previously-uploaded image files on the volume are corrupted
  // (confirmed: valid HTTP 200 + valid JPEG header bytes + plausible file
  // size, but the browser's decoder still fails — naturalWidth/Height stay
  // 0 after `complete` — likely truncated mid-download by an earlier
  // scraping session, unrelated to this feature). /api/unit-images-exist
  // only checks the file EXISTS, not that it decodes, so hasImage alone
  // isn't enough — a real onError on the actual <img> is the only reliable
  // signal, hence this hides the whole thumbnail on failure instead of
  // showing the browser's broken-image icon + wrapped alt text.
  const [imgFailed, setImgFailed] = useState(false);
  const measuredRef = useRef(false);

  useLayoutEffect(() => {
    if (!hasImage || measuredRef.current) return;
    measuredRef.current = true;
    const el = cardWrapRef.current?.querySelector('.gw-ability-card');
    if (!el) return;
    const naturalHeight = el.scrollHeight;
    const prevWidth = el.style.width;
    el.style.width = `calc(100% - ${UNIT_THUMB_RESERVED_PX}px)`;
    const shrunkHeight = el.scrollHeight;
    el.style.width = prevWidth;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 18;
    setFits(Math.round((shrunkHeight - naturalHeight) / lineHeight) <= 1);
  }, [hasImage]);

  const showThumb = hasImage && fits && !imgFailed;

  return (
    <div className="bb-fight-ability">
      <div className="bb-fight-ability-row" ref={cardWrapRef}>
        {/* ab.bullets is already a real array (it came from the OUTER
            JSON.parse of warscrolls.abilities, unlike faction-ability cards
            whose bullets are a raw unparsed JSON string) */}
        <AbilityCard ab={ab} keywords={[]} />
        {showThumb && (
          <div className="bb-unit-thumb">
            <img src={`/api/unit-image/${ab._unitId}`} alt={ab._unitName} className="bb-unit-thumb-img" onError={() => setImgFailed(true)} />
            <img src={`/api/unit-image/${ab._unitId}`} alt="" aria-hidden="true" className="bb-unit-thumb-zoom-img" />
          </div>
        )}
      </div>
    </div>
  );
}

function parseJsonArray(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

// ── AoS 4e battle-round phase ribbon. "match" reuses the same substring
// convention WarscrollGW's PHASE_PRESETS already uses against an ability's
// `timing` text — not exported from there, so duplicated here rather than
// reaching into that module's internals. The phase buttons are FILTERS, not
// sorts: a phase's list is exactly the abilities coded to that phase, full
// stop — Passive/Reaction/Once-Per-Turn|Battle|Battle-Round abilities are
// pulled into their own "always available" bucket instead (see
// splitAbilitiesForPhase), shown under a divider on every phase rather than
// mixed into the phase-specific list. ───────────────────────────────────────
const BATTLE_PHASES = [
  { key: 'deployment', label: 'Deployment', match: ['deployment'] },
  { key: 'hero',       label: 'Hero',       match: ['hero phase'] },
  { key: 'movement',   label: 'Movement',   match: ['movement', 'move phase'] },
  { key: 'shooting',   label: 'Shooting',   match: ['shooting'] },
  { key: 'charge',     label: 'Charge',     match: ['charge'] },
  { key: 'combat',     label: 'Combat',     match: ['combat'] },
  { key: 'end_of_turn',label: 'End of Turn',match: ['end of turn', 'end of any turn', 'end of battle'] },
];
const ALWAYS_MATCH = ['passive', 'reaction', 'any phase', 'once per turn', 'once per battle', 'once per battle round', 'start of battle round'];

// Site-wide global phase colors, copied from WarscrollGW's PHASE_PRESETS
// (not exported there) — used to color the ribbon buttons themselves.
const PHASE_COLORS = {
  deployment:   { hdrBg: '#280858', hdrTxt: '#c8b8f8', border: '#6040c0' },
  hero:         { hdrBg: '#7a6010', hdrTxt: '#ffffff', border: '#c8a020' },
  movement:     { hdrBg: '#0e4020', hdrTxt: '#a0f0b8', border: '#208848' },
  shooting:     { hdrBg: '#0c2a60', hdrTxt: '#b8d8ff', border: '#2060c8' },
  charge:       { hdrBg: '#6a2c00', hdrTxt: '#ffd898', border: '#c86010' },
  combat:       { hdrBg: '#6a0808', hdrTxt: '#ffd8d8', border: '#c02020' },
  end_of_turn:  { hdrBg: '#202020', hdrTxt: '#c0c0c0', border: '#505050' },
};

// Deliberately `ab.timing` only, NOT `ab.phase_key` — phase_key (present on
// Battle Formations) is a pre-computed *thematic* color hint for badge
// tinting elsewhere in the app, not a statement of which phase the ability
// can actually be used in. Using it here misclassified Passive formation
// abilities (like Akhelian Beastmasters' Ferocious Predators, phase_key
// "combat" for flavor even though its rules text is Passive) as
// combat-phase-specific instead of always-available.
function abilityPhaseKey(ab) {
  const t = (ab.timing || '').toLowerCase();
  if (!t) return null;
  const phase = BATTLE_PHASES.find(p => p.match.some(k => t.includes(k)));
  return phase ? phase.key : null;
}
function abilityIsAlwaysAvailable(ab) {
  const t = (ab.timing || '').toLowerCase();
  return !!t && ALWAYS_MATCH.some(k => t.includes(k));
}

// ab.phase_key (backend/phaseKey.js) is a comma-joined list of PHASE_PRESETS-
// style strings — e.g. "combat" or "movement,charge" or "hero phase,combat,
// shooting" — populated for any ambiguous-timing (Passive/Reaction/bare
// Once-Per-X) ability whose effect text still ties it to one or more actual
// phases (confirmed examples: Idoneth's Armour of the Cythai modifies combat
// rolls despite Passive timing; Hunter of Souls is picked at deployment but
// its anti-X bonus applies in combat AND shooting). Translated here into
// this file's own short BATTLE_PHASES keys for bucketing purposes; the raw
// string is left untouched on the ability object so AbilityCard's own
// getPhaseStyle(ab) can build the matching split banner independently.
function discoveredPhaseKeys(ab) {
  if (!ab.phase_key) return [];
  return ab.phase_key.split(',').map(k => k.trim().toLowerCase())
    .map(k => BATTLE_PHASES.find(p => p.match.some(m => k.includes(m)))?.key)
    .filter(Boolean);
}

// Splits a flat ability list into "exactly this phase" vs "always available"
// for a given ribbon selection — the two buckets a FightPane section renders.
// Mutually exclusive: an ability like "Once Per Turn (Army), Any Combat
// Phase" carries both a specific-phase word (combat) and an always-available
// one (once per turn) — treated as combat-phase-specific, not duplicated
// into the always-available bucket too, since it does have one real phase.
//
// An ambiguous-timing ability with discovered phase key(s) (see
// discoveredPhaseKeys — e.g. Ferocious Predators' text ties it to the combat
// phase even though its own timing is bare "Passive"; Hunter of Souls covers
// BOTH deployment and combat/shooting) shows up under EVERY one of its
// discovered phases, not just one — same split banner colouring regardless
// of which phase view it's currently showing under (AbilityCard reads the
// full multi-key ab.phase_key itself, unaffected by which bucket this put it in).
function splitAbilitiesForPhase(abilities, phaseKey) {
  const withKeys = abilities.map(ab => {
    const literal = abilityPhaseKey(ab);
    if (literal) return { ab, keys: [literal] };
    if (!abilityIsAlwaysAvailable(ab)) return { ab, keys: [], drop: true };
    const discovered = discoveredPhaseKeys(ab);
    return { ab, keys: discovered };
  });
  const inPhase = withKeys.filter(x => x.keys.includes(phaseKey)).map(x => x.ab);
  const always = withKeys.filter(x => x.keys.length === 0 && !x.drop).map(x => x.ab);
  return { inPhase, always };
}


const SOURCES = [
  { key: 'warscrolls',     label: 'Warscrolls' },
  { key: 'army_list',      label: 'Built Army List' },
  { key: 'spearhead',      label: 'Spearhead' },
  { key: 'path_to_glory',  label: 'Path to Glory' },
];

function makeBlankSide() {
  return {
    source: 'warscrolls',
    loading: false,
    error: null,
    units: [],
    factionSlug: null,
    factionName: null,
    battleFormation: null,
    factionRules: null,   // full /api/faction-rules/:slug response
    // Explicit selections, when the source actually has them (army_list /
    // path_to_glory) — null fields mean "no selection data, show everything
    // for the faction" rather than "nothing selected".
    selection: null,      // { heroic_traits: [names], artefacts: [names], spell_lore: [names], prayer_lore: [names], manifestation_lore: [names] } | null
  };
}

// ── Cross-device session resume (battle_buddy_state table) ─────────────────
// A side's state is snapshotted as a small "recipe" (which units, not the
// full unit records) rather than the raw `units` array — mirrors exactly
// what ArmyListSource/PathToGlorySource already store (a unitId list) so
// restoring is just the same /api/warscrolls?ids= lookup every source
// already does, and the row stays tiny and never goes stale relative to the
// live warscroll data.
function sideSnapshot(state) {
  return {
    source: state.source,
    unitIds: state.units.map(u => u.id),
    factionSlug: state.factionSlug,
    factionName: state.factionName,
    battleFormation: state.battleFormation,
    selection: state.selection,
    spearheadName: state.spearheadName || null,
    loadedListId: state.loadedListId || null,
    loadedRosterId: state.loadedRosterId || null,
  };
}

async function restoreSide(snapshot) {
  const blank = makeBlankSide();
  if (!snapshot) return blank;
  let units = [];
  if (snapshot.unitIds && snapshot.unitIds.length > 0) {
    try {
      const { data } = await axios.get(`/api/warscrolls?ids=${snapshot.unitIds.join(',')}&pageSize=300&sortBy=faction`);
      units = data.data || [];
    } catch { units = []; }
  }
  return {
    ...blank,
    source: snapshot.source || 'warscrolls',
    units,
    factionSlug: snapshot.factionSlug || null,
    factionName: snapshot.factionName || null,
    battleFormation: snapshot.battleFormation || null,
    selection: snapshot.selection || null,
    spearheadName: snapshot.spearheadName || undefined,
    loadedListId: snapshot.loadedListId || undefined,
    loadedRosterId: snapshot.loadedRosterId || undefined,
  };
}

// ── Faction rules fetch/cache — shared across both panes so picking the
// same faction on both sides (mirror matches, proxy games) doesn't double-
// fetch. ─────────────────────────────────────────────────────────────────
function useFactionRules() {
  const cache = useRef({});
  const [, force] = useState(0);
  const fetchFor = useCallback(async (slug) => {
    if (!slug || cache.current[slug]) return cache.current[slug] ?? null;
    cache.current[slug] = null; // placeholder to dedupe concurrent calls
    try {
      const { data } = await axios.get(`/api/faction-rules/${slug}`);
      cache.current[slug] = data;
      force(n => n + 1);
      return data;
    } catch {
      return null;
    }
  }, []);
  return { get: slug => cache.current[slug] ?? null, fetchFor };
}

// ── Core Rules Command abilities — universal, not faction-specific, so a
// single fetch (not per-slug like faction rules) shared by both panes.
function useCommandAbilities() {
  const [abilities, setAbilities] = useState(null); // null = loading, [] = loaded-empty
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/command-abilities')
      .then(({ data }) => { if (!cancelled) setAbilities(data); })
      .catch(() => { if (!cancelled) setAbilities([]); });
    return () => { cancelled = true; };
  }, []);
  return abilities;
}

// ── Warscrolls source: whatever's currently flagged Friendly/Enemy on the
// Warscrolls page (server-backed via user_units, not localStorage), further
// narrowed by that page's Friendly/Enemy Faction dropdowns (which ARE only
// in localStorage, under 'aos-filters' — see WarscrollsPage.js's FILTER_KEY).
// Mirrors WarscrollsPage's own combined-filter behavior exactly: either
// dropdown alone restricts BOTH sides' flagged units, not just its own side
// (see feedback_backend_faction_filter_combined memory) — so without this,
// Battle Buddy would show flagged units the Warscrolls table itself has
// already filtered out.
function activeFactionSlugsFromWarscrollsFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem('aos-filters')) || {};
    return [saved.faction, saved.enemyFaction].filter(Boolean);
  } catch { return []; }
}

function WarscrollsSource({ side, state, setState, factionRules }) {
  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const { data: userUnits } = await axios.get('/api/user-units');
        const flag = side === 'friendly' ? 'is_friendly' : 'is_enemy';
        const ids = userUnits.filter(u => u[flag]).map(u => u.warscroll_id);
        if (ids.length === 0) {
          if (!cancelled) setState(s => ({ ...s, loading: false, units: [], factionSlug: null, factionName: null }));
          return;
        }
        const { data } = await axios.get(`/api/warscrolls?ids=${ids.join(',')}&pageSize=300&sortBy=faction`);
        if (cancelled) return;
        // user_units doesn't carry faction_slug, so the faction filter can only
        // be applied here, against the /api/warscrolls response.
        const activeFactionSlugs = activeFactionSlugsFromWarscrollsFilters();
        const units = activeFactionSlugs.length > 0
          ? (data.data || []).filter(u => activeFactionSlugs.includes(u.faction_slug))
          : (data.data || []);
        const slug = units[0]?.faction_slug ?? null;
        const name = units[0]?.faction ?? null;
        setState(s => ({ ...s, loading: false, units, factionSlug: slug, factionName: name, battleFormation: null, selection: null }));
        if (slug) factionRules.fetchFor(slug);
      } catch (err) {
        if (!cancelled) setState(s => ({ ...s, loading: false, error: 'Failed to load flagged units.' }));
      }
    })();
    return () => { cancelled = true; };
  }, [side]); // eslint-disable-line

  if (state.loading) return <div className="bb-pane-empty">Loading…</div>;
  if (state.error) return <div className="bb-pane-empty">{state.error}</div>;
  if (state.units.length === 0) {
    return (
      <div className="bb-pane-empty">
        No units flagged {side === 'friendly' ? 'Friendly' : 'Enemy'} on the <Link to="/warscrolls">Warscrolls</Link> page yet.
      </div>
    );
  }
  return <UnitList units={state.units} />;
}

// ── Built Army List source — Army Builder's List/Enemy List pulldowns save
// into the same table tagged by list_type ('own' vs 'enemy'); this side's
// pool is picked to match, same as the Warscrolls source's flag filtering. ──
function ArmyListSource({ side, state, setState, factionRules }) {
  const [lists, setLists] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const wantType = side === 'friendly' ? 'own' : 'enemy';

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/army-builder-lists').then(({ data }) => {
      if (!cancelled) setLists(data.filter(l => (l.list_type || 'own') === wantType));
    }).catch(() => { if (!cancelled) setLists([]); });
    return () => { cancelled = true; };
  }, [wantType]);

  const load = async (id) => {
    setLoadingId(id);
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const { data: full } = await axios.get(`/api/army-builder-lists/${id}`);
      const blob = full.data || {};
      const unitIds = Object.keys(blob.roster || {});
      let units = [];
      if (unitIds.length > 0) {
        const { data: uData } = await axios.get(`/api/warscrolls?ids=${unitIds.join(',')}&pageSize=300&sortBy=faction`);
        units = uData.data || [];
      }
      const heroAssignments = blob.heroAssignments || {};
      const collectAssigned = key => [...new Set(Object.values(heroAssignments).map(h => h?.[key]).filter(Boolean))];
      // Reverse lookup (ability name -> assigned hero's unit name), so the
      // card can show who's actually carrying it — only Heroic Traits/
      // Artefacts are ever assigned to a specific hero here; Spell/Prayer/
      // Manifestation Lore are army-wide, not per-unit (see LoreCardsStage).
      const buildAssignedTo = key => {
        const map = {};
        for (const [heroId, assign] of Object.entries(heroAssignments)) {
          const name = assign?.[key];
          if (!name) continue;
          const unit = units.find(u => String(u.id) === String(heroId));
          if (unit) map[name.toUpperCase()] = unit.name;
        }
        return map;
      };
      const selection = {
        heroic_traits:      collectAssigned('heroic_traits'),
        artefacts:           collectAssigned('artefacts'),
        spell_lore:          collectAssigned('spell_lore'),
        prayer_lore:         collectAssigned('prayer_lore'),
        manifestation_lore:  collectAssigned('manifestation_lore'),
        assignedTo: {
          heroic_traits: buildAssignedTo('heroic_traits'),
          artefacts: buildAssignedTo('artefacts'),
        },
      };
      setState(s => ({
        ...s, loading: false,
        units,
        factionSlug: blob.faction || full.faction_slug || null,
        factionName: full.faction_name || null,
        battleFormation: blob.battleFormation || null,
        selection,
        loadedListId: id,
      }));
      if (blob.faction) factionRules.fetchFor(blob.faction);
    } catch {
      setState(s => ({ ...s, loading: false, error: 'Failed to load that list.' }));
    } finally {
      setLoadingId(null);
    }
  };

  if (lists === null) return <div className="bb-pane-empty">Loading…</div>;
  if (lists.length === 0) {
    return (
      <div className="bb-pane-empty">
        No saved {wantType === 'enemy' ? 'Enemy Lists' : 'lists'} yet — build one on the <Link to="/army-builder">Army Builder</Link> page first
        {wantType === 'enemy' ? ' (the Enemy List pulldown)' : ''}.
      </div>
    );
  }
  return (
    <div className="bb-source-list">
      {lists.map(l => (
        <div key={l.id} className="bb-source-list-row">
          <span className="bb-source-list-name">{l.name}</span>
          <span className="bb-source-list-faction">{l.faction_name || '—'}</span>
          <button type="button" className="bb-load-btn" disabled={loadingId === l.id} onClick={() => load(l.id)}>
            {loadingId === l.id ? 'Loading…' : (state.units.length > 0 && state.loadedListId === l.id ? 'Loaded' : 'Load')}
          </button>
        </div>
      ))}
      {state.units.length > 0 && <UnitList units={state.units} />}
    </div>
  );
}

// ── Spearhead source ────────────────────────────────────────────────────────
function SpearheadSource({ side, state, setState, factionRules }) {
  const [spearheads, setSpearheads] = useState(null);
  const [allUnits, setAllUnits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: rules }, { data: unitsRes }] = await Promise.all([
          axios.get('/api/spearheads'),
          axios.get('/api/warscrolls?spearheadOnly=1&sortBy=faction&sortDir=asc&pageSize=9999&page=1'),
        ]);
        if (cancelled) return;
        setSpearheads(rules);
        setAllUnits(unitsRes.data || []);
        // Prefill from whatever the Spearhead page itself has saved, if
        // anything — the only place this pairing is remembered at all
        // (no server-side concept of a "current spearhead" exists).
        try {
          const raw = JSON.parse(localStorage.getItem('aos-sp-filters'));
          const already = side === 'friendly' ? raw?.yourSpearhead : raw?.opponentSpearhead;
          if (already && rules.some(r => r.name === already)) pick(already, rules, unitsRes.data || []);
        } catch {}
      } catch {
        if (!cancelled) { setSpearheads([]); setAllUnits([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const pick = (name, rulesList, unitsList) => {
    const rules = (rulesList ?? spearheads).find(r => r.name === name);
    const units = (unitsList ?? allUnits).filter(u => (u.spearhead || '').split('|').includes(name));
    setState(s => ({
      ...s,
      units,
      factionSlug: rules?.faction_slug ?? null,
      factionName: units[0]?.faction ?? null,
      battleFormation: null,
      selection: null,
      spearheadName: name,
    }));
    if (rules?.faction_slug) factionRules.fetchFor(rules.faction_slug);
  };

  if (spearheads === null) return <div className="bb-pane-empty">Loading…</div>;
  return (
    <div className="bb-source-list">
      <select
        className="bb-spearhead-select"
        value={state.spearheadName || ''}
        onChange={e => e.target.value && pick(e.target.value)}
      >
        <option value="">— Choose a Spearhead —</option>
        {spearheads.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
      </select>
      {state.units.length > 0 && <UnitList units={state.units} />}
    </div>
  );
}

// ── Path to Glory source ────────────────────────────────────────────────────
function PathToGlorySource({ side, state, setState, factionRules }) {
  const [rosters, setRosters] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const autoLoadedRef = useRef(false);

  const load = useCallback(async (id) => {
    setLoadingId(id);
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const { data: full } = await axios.get(`/api/ptg-rosters/${id}`);
      const blob = full.data || {};
      const regimentUnitIds = (blob.regiments || []).flatMap(r => r.units || []);
      const auxIds = (blob.auxUnits || []).map(u => u.warscrollId || u.id).filter(Boolean);
      const unitIds = [...new Set([...regimentUnitIds, ...auxIds])];
      let units = [];
      if (unitIds.length > 0) {
        const { data: uData } = await axios.get(`/api/warscrolls?ids=${unitIds.join(',')}&pageSize=300&sortBy=faction`);
        units = uData.data || [];
      }
      const selection = {
        heroic_traits: [],
        // warlordEnhancements/warlordPathAbility are free-text fields on the
        // printed roster (not structured picks from the faction-rules DB —
        // there's no real "Path Ability" table to match against), so they
        // can't be name-matched into rules.artefacts like the lores below.
        // Surfaced separately as plain text under their own sub-heading.
        artefacts: (blob.warlordEnhancements || '').split(',').map(s => s.trim()).filter(Boolean),
        pathAbilityText: blob.warlordPathAbility || '',
        spell_lore: (blob.spellLore || []).filter(Boolean),
        prayer_lore: (blob.prayerLore || []).filter(Boolean),
        manifestation_lore: (blob.manifestationLore || []).filter(Boolean),
      };
      setState(s => ({
        ...s, loading: false,
        units,
        factionSlug: blob.faction || full.faction_slug || null,
        factionName: full.faction_name || null,
        battleFormation: blob.battleFormation || null,
        selection,
        loadedRosterId: id,
      }));
      if (blob.faction) factionRules.fetchFor(blob.faction);
    } catch {
      setState(s => ({ ...s, loading: false, error: 'Failed to load that roster.' }));
    } finally {
      setLoadingId(null);
    }
  }, [setState, factionRules]);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/ptg-rosters').then(({ data }) => { if (!cancelled) setRosters(data); }).catch(() => { if (!cancelled) setRosters([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (rosters && rosters.length === 1 && !autoLoadedRef.current) {
      autoLoadedRef.current = true;
      load(rosters[0].id);
    }
  }, [rosters, load]);

  if (rosters === null) return <div className="bb-pane-empty">Loading…</div>;
  if (rosters.length === 0) {
    return <div className="bb-pane-empty">No Path to Glory rosters yet — create one on the <Link to="/path-to-glory">Path to Glory</Link> page first.</div>;
  }
  return (
    <div className="bb-source-list">
      {rosters.length > 1 && rosters.map(r => (
        <div key={r.id} className="bb-source-list-row">
          <span className="bb-source-list-name">{r.name}</span>
          <span className="bb-source-list-faction">{r.faction_name || '—'}</span>
          <button type="button" className="bb-load-btn" disabled={loadingId === r.id} onClick={() => load(r.id)}>
            {loadingId === r.id ? 'Loading…' : (state.loadedRosterId === r.id ? 'Loaded' : 'Load')}
          </button>
        </div>
      ))}
      {state.units.length > 0 && <UnitList units={state.units} />}
    </div>
  );
}

function UnitList({ units }) {
  return (
    <div className="bb-unit-list">
      {units.map(u => (
        <div key={u.id} className="bb-unit-row">
          <span className="bb-unit-row-name">{u.name}</span>
          <span className="bb-unit-row-pts">{u.points}pts</span>
        </div>
      ))}
    </div>
  );
}

function SourcePane({ side, state, setState, factionRules }) {
  return (
    <div className="bb-pane">
      <div className="bb-pane-title">{side === 'friendly' ? 'Friendly' : 'Enemy'}</div>
      <div className="bb-source-row">
        {SOURCES.map(s => (
          <button
            key={s.key}
            className={`bb-source-btn${state.source === s.key ? ' active' : ''}`}
            onClick={() => setState(prev => ({ ...makeBlankSide(), source: s.key }))}
          >{s.label}</button>
        ))}
      </div>
      {state.source === 'warscrolls'    && <WarscrollsSource    side={side} state={state} setState={setState} factionRules={factionRules} />}
      {state.source === 'army_list'     && <ArmyListSource      side={side} state={state} setState={setState} factionRules={factionRules} />}
      {state.source === 'spearhead'     && <SpearheadSource     side={side} state={state} setState={setState} factionRules={factionRules} />}
      {state.source === 'path_to_glory' && <PathToGlorySource   side={side} state={state} setState={setState} factionRules={factionRules} />}
    </div>
  );
}

// ── Fight! stage ─────────────────────────────────────────────────────────────
// Faction Abilities used to be one flat merged list — now kept as separate
// per-category sections (matching the book's own section names) so each can
// get its own sub-heading and collapse triangle instead of one undifferentiated
// wall of cards.
const FACTION_SECTIONS = [
  { key: 'traits',             label: 'Battle Traits' },
  { key: 'formations',         label: 'Battle Formations' },
  { key: 'heroic_traits',      label: 'Heroic Traits' },
  { key: 'artefacts',          label: 'Artefacts of Power' },
  { key: 'spell_lore',         label: 'Spell Lore' },
  { key: 'prayer_lore',        label: 'Prayer Lore' },
  { key: 'manifestation_lore', label: 'Manifestations' },
];

function collectFactionSections(state, factionRulesFor, hiddenSeasons) {
  const rules = factionRulesFor(state.factionSlug);
  if (!rules) return [];
  const sel = state.selection;
  const pick = (arr, names) => (names === undefined || names === null ? arr : arr.filter(a => names.includes(a.name)));
  const filterOutdated = arr => arr.filter(a => !hiddenSeasons[abilitySeason(a)]);
  const fixed = FACTION_SECTIONS.map(s => {
    let abilities;
    if (s.key === 'formations') {
      abilities = state.battleFormation ? rules.formations.filter(f => f.formation_name === state.battleFormation) : rules.formations;
    } else if (s.key === 'traits') {
      abilities = rules.traits; // Battle Traits aren't narrowed by a selection, unlike Heroic Traits/Artefacts/Lores
    } else {
      abilities = pick(rules[s.key], sel?.[s.key]);
    }
    abilities = filterOutdated(abilities || []);
    // Heroic Traits/Artefacts can be assigned to a specific hero (Army
    // Builder's heroAssignments) — surfaced as a suffix on the card, same
    // idea as the source-note tag, so it's visible without cross-checking
    // the roster separately.
    const assignedMap = sel?.assignedTo?.[s.key];
    if (assignedMap) abilities = abilities.map(a => ({ ...a, assigned_unit_name: assignedMap[a.name] || null }));
    return { ...s, abilities };
  });
  // Faction/supplement-specific tables under their own printed heading
  // (scrapeRules.js's findExtraSectionTitles) — e.g. a Scourge of Aqshy/
  // Ghyran non-Hero enhancement table. Not narrowed by `sel` (no existing
  // hero-assignment concept covers these; they're for non-Hero units), and
  // split into one section per distinct group_name since a faction can
  // publish more than one (Cities of Sigmar has 3).
  const otherByGroup = [];
  const otherIndex = {};
  for (const item of rules.other || []) {
    const gName = item.group_name || 'Other';
    if (!otherIndex[gName]) {
      otherIndex[gName] = { key: `other:${gName}`, label: gName, abilities: [] };
      otherByGroup.push(otherIndex[gName]);
    }
    otherIndex[gName].abilities.push(item);
  }
  for (const s of otherByGroup) s.abilities = filterOutdated(s.abilities);
  return [...fixed, ...otherByGroup];
}

// Some Battle Traits are grouped under a shared column-header label instead of
// standing alone (confirmed: Idoneth Deepkin's "Tides of the Sea"/"Tides of
// the Storm") — the book prints these as two (or more) light-blue/gold-banner
// columns in a fixed order, not a flat list. Mirrors WarscrollGW.js's
// TraitGroupColumn/FactionTraitsSlide grouping so it reads the same here.
function groupTraits(abilities) {
  const ungrouped = abilities.filter(t => !t.group_name);
  const groups = [];
  const nameToGroup = {};
  for (const t of abilities) {
    if (!t.group_name) continue;
    if (!nameToGroup[t.group_name]) {
      nameToGroup[t.group_name] = { name: t.group_name, items: [] };
      groups.push(nameToGroup[t.group_name]);
    }
    nameToGroup[t.group_name].items.push(t);
  }
  return { groups, ungrouped };
}

// Shared collapse-triangle wrapper for every "section with a separator" in the
// Fight view — Unit/Faction/Command Point Abilities, and each Faction
// Abilities sub-heading. Local (uncontrolled) open/closed state per instance,
// so friendly/enemy panes (and each sub-section) collapse independently.
function CollapsibleSection({ title, count, defaultOpen = true, level = 'top', children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`bb-collapsible bb-collapsible-${level}`}>
      <button type="button" className="bb-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span className={`bb-collapse-triangle${open ? ' open' : ''}`}>▶</span>
        <span className="bb-collapsible-title">{title}</span>
        {count != null && <span className="bb-collapsible-count">{count}</span>}
      </button>
      {open && <div className="bb-collapsible-body">{children}</div>}
    </div>
  );
}

// Renders one of the ability sections: the phase-specific list, then
// (if any) a divider and the "Passive / Any Phase" list that's the same
// regardless of which ribbon phase is selected.
// wideFirstIfOdd mimics the printed book's Spell/Prayer/Manifestation Lore
// layout (confirmed: Idoneth's 3-spell "Lore of the Tides" — Steed of Tides
// spans the full top row, Counter-Current/Pressure of the Deep sit below
// it as a pair) rather than a plain 2-col grid, which instead leaves a
// lonely last card with an empty cell beside it whenever the count is odd.
// Only kicks in for an odd count — an even count already tiles cleanly.
function AbilitySection({ title, inPhase, always, renderCard, defaultOpen = true, level = 'top', wideFirstIfOdd = false }) {
  const hasAny = inPhase.length > 0 || always.length > 0;
  // Nothing for the current phase — collapse the whole section (heading
  // included) rather than showing an empty placeholder; a section with
  // some cards just none active this phase reads the same as a section the
  // faction has nothing in at all, so there's no information lost.
  if (!hasAny) return null;
  const gridClass = list => `gw-abilities-grid bb-fight-grid${wideFirstIfOdd && list.length % 2 === 1 ? ' bb-fight-grid-wide-first' : ''}`;
  return (
    <CollapsibleSection title={title} count={inPhase.length + always.length} defaultOpen={defaultOpen} level={level}>
      {inPhase.length > 0 && (
        <div className={gridClass(inPhase)}>{inPhase.map(renderCard)}</div>
      )}
      {inPhase.length > 0 && always.length > 0 && <div className="gw-formation-divider" />}
      {always.length > 0 && (
        <>
          <div className="bb-fight-section-subtitle">Passive / Any Phase</div>
          <div className={gridClass(always)}>{always.map(renderCard)}</div>
        </>
      )}
    </CollapsibleSection>
  );
}

// Groups a unit-ability list (Unit Abilities / Regiment of Renown) by
// _unitName so each unit starts a fresh row instead of a flat 2-col grid
// interleaving different units' cards into the same row (e.g. a unit with
// an odd number of abilities left its last card sharing a row with the
// NEXT unit's first card). Each unit's own cards still share a 2-col grid
// with each other — there's no over-narrowing risk here like the Battle
// Formations fix, since a unit's row spans the full pane width, not a
// pre-halved column.
function groupUnitAbilities(abilities) {
  const groups = [];
  const nameToGroup = {};
  for (const item of abilities) {
    const uName = item._unitName || '';
    if (!nameToGroup[uName]) {
      nameToGroup[uName] = { name: uName, items: [] };
      groups.push(nameToGroup[uName]);
    }
    nameToGroup[uName].items.push(item);
  }
  return groups;
}

function UnitAbilitiesSection({ title, inPhase, always, renderCard }) {
  const hasAny = inPhase.length > 0 || always.length > 0;
  if (!hasAny) return null;
  // The unit name renders once per group (not once per card, like a plain
  // AbilitySection would via UnitAbilityCard) — indented below it so the
  // parent/child relationship reads visually, and boxed only when there's
  // more than 1 card so a single-ability unit doesn't get an empty-feeling
  // border around just one card.
  const renderBucket = (list) => (
    <div className="bb-unit-ability-groups">
      {groupUnitAbilities(list).map((group, gi) => (
        <div className="bb-unit-ability-group" key={gi}>
          <div className="bb-fight-ability-unit">{group.name}</div>
          <div className={`bb-unit-ability-cards${group.items.length > 1 ? ' bb-unit-ability-cards-boxed' : ''}`}>
            <div className="gw-abilities-grid bb-fight-grid">{group.items.map(renderCard)}</div>
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <CollapsibleSection title={title} count={inPhase.length + always.length}>
      {inPhase.length > 0 && renderBucket(inPhase)}
      {inPhase.length > 0 && always.length > 0 && <div className="gw-formation-divider" />}
      {always.length > 0 && (
        <>
          <div className="bb-fight-section-subtitle">Passive / Any Phase</div>
          {renderBucket(always)}
        </>
      )}
    </CollapsibleSection>
  );
}

// Battle Traits get grouped-column rendering (book order + banner headers)
// instead of a flat card grid — same idea as AbilitySection but splitting
// each of inPhase/always by group_name first.
function BattleTraitsSection({ inPhase, always, renderCard }) {
  const hasAny = inPhase.length > 0 || always.length > 0;
  if (!hasAny) return null;
  const renderBucket = (list) => {
    const { groups, ungrouped } = groupTraits(list);
    return (
      <>
        {groups.length > 0 && (
          <div className="gw-trait-groups" style={{ gridTemplateColumns: `repeat(${groups.length}, 1fr)` }}>
            {groups.map((group, gi) => (
              <div className="gw-trait-group" data-col={gi % 2 === 0 ? 'a' : 'b'} key={gi}>
                <div className="gw-trait-group-header">{group.name}</div>
                <div className="gw-abilities-grid">{group.items.map(renderCard)}</div>
              </div>
            ))}
          </div>
        )}
        {groups.length > 0 && ungrouped.length > 0 && <div className="gw-formation-divider" />}
        {ungrouped.length > 0 && (
          <div className="gw-abilities-grid bb-fight-grid">{ungrouped.map(renderCard)}</div>
        )}
      </>
    );
  };
  return (
    <CollapsibleSection title="Battle Traits" count={inPhase.length + always.length} level="sub">
      {inPhase.length > 0 && renderBucket(inPhase)}
      {inPhase.length > 0 && always.length > 0 && <div className="gw-formation-divider" />}
      {always.length > 0 && (
        <>
          <div className="bb-fight-section-subtitle">Passive / Any Phase</div>
          {renderBucket(always)}
        </>
      )}
    </CollapsibleSection>
  );
}

// Groups Battle Formation abilities by formation_name so the card's actual
// source is visible (e.g. "Outflank the Enemy" belongs to the "Deep-sea
// Stalkers" formation) — previously rendered as one undifferentiated flat
// list with no indication of which named formation granted each ability.
function groupFormations(abilities) {
  const groups = [];
  const nameToGroup = {};
  for (const item of abilities) {
    const gName = item.formation_name || 'General';
    if (!nameToGroup[gName]) {
      nameToGroup[gName] = { name: gName, sourceNote: item.source_note || null, items: [] };
      groups.push(nameToGroup[gName]);
    }
    nameToGroup[gName].items.push(item);
  }
  return groups;
}

function BattleFormationsSection({ inPhase, always, renderCard }) {
  const hasAny = inPhase.length > 0 || always.length > 0;
  if (!hasAny) return null;
  const renderBucket = (list) => (
    <div className="gw-formation-groups-2col">
      {groupFormations(list).map((group, gi) => (
        <div className="gw-formation-group" key={gi}>
          {group.name !== 'General' && (
            <div className="gw-formation-group-header">
              {group.name}
              {group.sourceNote && <span className="gw-formation-source-note"> ({group.sourceNote})</span>}
            </div>
          )}
          <div className="gw-abilities-grid">{group.items.map(renderCard)}</div>
        </div>
      ))}
    </div>
  );
  return (
    <CollapsibleSection title="Battle Formations" count={inPhase.length + always.length} level="sub">
      {inPhase.length > 0 && renderBucket(inPhase)}
      {inPhase.length > 0 && always.length > 0 && <div className="gw-formation-divider" />}
      {always.length > 0 && (
        <>
          <div className="bb-fight-section-subtitle">Passive / Any Phase</div>
          {renderBucket(always)}
        </>
      )}
    </CollapsibleSection>
  );
}

function FactionAbilitiesGroup({ state, factionRulesFor, phaseKey, renderCard, hiddenSeasons }) {
  const sections = collectFactionSections(state, factionRulesFor, hiddenSeasons);
  const pathText = (state.selection?.pathAbilityText || '').trim();
  const hasAnySection = sections.some(s => s.abilities.length > 0) || pathText;
  if (!hasAnySection) return null;

  return (
    <>
      {sections.map(s => {
        // A category the faction has NOTHING in at all (e.g. Idoneth Deepkin
        // publish no Prayer Lore) is suppressed entirely — and, per each
        // section component above, so is one that has cards but none active
        // for the current phase, so there's nothing further to gate here.
        if (s.abilities.length === 0) return null;
        const split = splitAbilitiesForPhase(s.abilities, phaseKey);
        if (s.key === 'traits') {
          return <BattleTraitsSection key={s.key} inPhase={split.inPhase} always={split.always} renderCard={renderCard} />;
        }
        if (s.key === 'formations') {
          return <BattleFormationsSection key={s.key} inPhase={split.inPhase} always={split.always} renderCard={renderCard} />;
        }
        return (
          <AbilitySection key={s.key} title={s.label} inPhase={split.inPhase} always={split.always} renderCard={renderCard} level="sub" wideFirstIfOdd={s.key === 'spell_lore'} />
        );
      })}
      {pathText && (
        <CollapsibleSection title="Path Abilities" level="sub">
          <div className="bb-path-ability-text">{pathText}</div>
        </CollapsibleSection>
      )}
    </>
  );
}

// Splits a side's selected units into normal army units vs. Regiment of
// Renown units (is_regiment_of_renown, see scraper.js's
// recomputeRegimentOfRenown — inferred from being shared across 3+
// factions, since Wahapedia has no explicit flag for this), each with
// Faction Terrain sorted last within its own group. RoR units get their own
// "Regiment of Renown" sub-heading rather than mixing anonymously into the
// normal Unit Abilities list.
// "Scourge of X" names a seasonal battle-profile variant. Each known season
// gets its own independently-toggleable "Hide" checkbox (SEASONS below)
// rather than one blanket switch, since e.g. Ghyran (previous season) and
// Aqshy (current, per server.js's TITLE_PREFIXES) aren't equally "outdated"
// — a user may want the current season's variants visible while still
// hiding the old one.
const SEASON_UNIT_PREFIXES = {
  ghyran: 'scourge of ghyran',
  aqshy: 'scourge of aqshy',
};
const SEASON_LABELS = {
  ghyran: 'Scourge of Ghyran',
  aqshy: 'Scourge of Aqshy',
};
function unitSeason(unit) {
  const n = (unit.name || '').toLowerCase();
  return Object.keys(SEASON_UNIT_PREFIXES).find(season => n.startsWith(SEASON_UNIT_PREFIXES[season])) || null;
}

// Faction-ability equivalent of unitSeason above, for Battle Traits/
// Formations/Heroic Traits/Artefacts/Lores. source_note (scrapeRules.js's
// extractSourceNote/extractH3Marker) comes from Wahapedia's own "Expansion.
// Scourge of X - ..." tooltip — flat sections (Heroic Traits/Artefacts/
// Spell/Prayer/Manifestation Lore) can group several items under a named
// preceding-sibling h3 (e.g. Idoneth's "Champions of the Tides"/"Leaders of
// the Raid") that a naive per-item descendant search can't see; the scraper
// now tracks that h3 forward and applies it by exact member-name match (see
// collectSectionBlocks), so this is derivable for every faction now. Kept as
// an empty fallback (same "book-confirmed, not derivable from text"
// precedent as phaseKey.js's MANUAL_OVERRIDES) in case a future item is
// genuinely unmarked on the page.
const MANUAL_SEASON_ABILITY_NAMES = {
  ghyran: new Set(),
  aqshy: new Set(),
};
function abilitySeason(ab) {
  const note = (ab.source_note || '').toLowerCase();
  const bySource = Object.keys(SEASON_UNIT_PREFIXES).find(season => note.includes(season));
  if (bySource) return bySource;
  const upperName = (ab.name || '').toUpperCase();
  return Object.keys(MANUAL_SEASON_ABILITY_NAMES).find(season => MANUAL_SEASON_ABILITY_NAMES[season].has(upperName)) || null;
}

function splitUnitsByRoR(units, hiddenSeasons) {
  const current = units.filter(u => !hiddenSeasons[unitSeason(u)]);
  const sortTerrainLast = list => [...list].sort((a, b) => (a.is_terrain ? 1 : 0) - (b.is_terrain ? 1 : 0));
  return {
    normal: sortTerrainLast(current.filter(u => !u.is_regiment_of_renown)),
    ror: sortTerrainLast(current.filter(u => u.is_regiment_of_renown)),
  };
}

function unitsToAbilities(units) {
  return units.flatMap(u => parseJsonArray(u.abilities).map(ab => ({ ...ab, _unitName: u.name, _unitId: u.id })));
}

function FightPane({ side, state, factionRulesFor, phaseKey, commandAbilities, hiddenSeasons }) {
  const { normal: normalUnits, ror: rorUnits } = splitUnitsByRoR(state.units, hiddenSeasons);
  const unitSplit = splitAbilitiesForPhase(unitsToAbilities(normalUnits), phaseKey);
  const rorSplit = splitAbilitiesForPhase(unitsToAbilities(rorUnits), phaseKey);

  // Which of this side's units actually have a resolvable thumbnail —
  // fetched once as a batch (existing /api/unit-images-exist, same one the
  // ImageLightbox uses to skip imageless units) rather than probing each
  // card's own <img> individually.
  const allUnitIds = [...normalUnits, ...rorUnits].map(u => u.id).filter(Boolean);
  const [imageIds, setImageIds] = useState(new Set());
  useEffect(() => {
    if (allUnitIds.length === 0) { setImageIds(new Set()); return; }
    let cancelled = false;
    axios.post('/api/unit-images-exist', { ids: allUnitIds })
      .then(({ data }) => { if (!cancelled) setImageIds(new Set(data.ids || [])); })
      .catch(() => { if (!cancelled) setImageIds(new Set()); });
    return () => { cancelled = true; };
  }, [allUnitIds.join(',')]); // eslint-disable-line

  const renderUnitCard = (ab, i) => <UnitAbilityCard key={i} ab={ab} hasImage={imageIds.has(ab._unitId)} />;
  const renderFactionCard = (ab, i) => (
    <AbilityCard key={i} ab={{ ...ab, bullets: parseJsonArray(ab.bullets) }} keywords={[]} />
  );
  const renderCommandCard = (ab, i) => (
    <AbilityCard key={i} ab={{ ...ab, bullets: parseJsonArray(ab.bullets), cost: ab.cp_cost ? `${ab.cp_cost} CP` : null }} keywords={[]} />
  );
  const commandSplit = splitAbilitiesForPhase(commandAbilities || [], phaseKey);

  return (
    <div className="bb-fight-pane">
      <div className="bb-fight-pane-title">{side === 'friendly' ? 'Friendly' : 'Enemy'}</div>

      <UnitAbilitiesSection title="Unit Abilities" inPhase={unitSplit.inPhase} always={unitSplit.always} renderCard={renderUnitCard} />
      {rorUnits.length > 0 && (
        <>
          <div className="gw-formation-divider" />
          <UnitAbilitiesSection title="Regiment of Renown" inPhase={rorSplit.inPhase} always={rorSplit.always} renderCard={renderUnitCard} />
        </>
      )}
      <div className="gw-formation-divider" />
      <CollapsibleSection title="Faction Abilities">
        <FactionAbilitiesGroup state={state} factionRulesFor={factionRulesFor} phaseKey={phaseKey} renderCard={renderFactionCard} hiddenSeasons={hiddenSeasons} />
      </CollapsibleSection>
      <div className="gw-formation-divider" />

      {commandAbilities === null ? (
        <div className="bb-fight-section">
          <div className="bb-fight-section-title">Command Point Abilities</div>
          <div className="bb-pane-empty">Loading…</div>
        </div>
      ) : (
        <AbilitySection title="Command Point Abilities" inPhase={commandSplit.inPhase} always={commandSplit.always} renderCard={renderCommandCard} />
      )}
    </div>
  );
}

// Shared between the "Vertical" layout's left sidebar and the "Toggle"
// layout's full-panel phase picker — every phase button is always fully
// filled with that phase's own site-wide global color + white text
// (outline-only inactive buttons read too low-contrast); the active phase
// is marked with a brighter border + lift instead of a color/fill change.
function PhaseRibbon({ phaseKey, setPhaseKey, onPick }) {
  return (
    <div className="bb-phase-ribbon">
      {BATTLE_PHASES.map(p => {
        const c = PHASE_COLORS[p.key];
        const active = phaseKey === p.key;
        return (
          <button
            key={p.key}
            className={`bb-phase-btn${active ? ' active' : ''}`}
            style={{ background: c.hdrBg, color: '#ffffff', borderColor: active ? '#ffffff' : c.border }}
            onClick={() => { setPhaseKey(p.key); onPick && onPick(); }}
          >{p.label}</button>
        );
      })}
    </div>
  );
}

// ── Warscrolls view: the selected army's units as cards; clicking one opens the
// full warscroll (prev/next steps through that side's army, and the split
// Friendly | Enemy view is available when both sides have units). ─────────────
function ArmyWarscrollsSide({ side, units, onOpen }) {
  return (
    <div className="bb-ws-side">
      <div className="bb-fight-pane-title">{side === 'friendly' ? 'Friendly' : 'Enemy'}</div>
      {units.length === 0 ? (
        <div className="bb-pane-empty">No units selected for this side yet (Step 1).</div>
      ) : (
        <div className="bb-ws-grid">
          {units.map(u => (
            <button type="button" key={u.id} className={`bb-ws-card ${side}`} onClick={() => onOpen(side, u)}>
              <span className="bb-ws-thumb">
                <img src={`${axios.defaults.baseURL || ''}/api/unit-image/${u.id}`} alt="" loading="lazy" onError={e => { e.target.style.visibility = 'hidden'; }} />
              </span>
              <span className="bb-ws-info">
                <span className="bb-ws-name">{u.name}</span>
                <span className="bb-ws-stats">
                  <span title="Move">Mv {u.move || '—'}</span>
                  <span title="Health">HP {u.health || '—'}</span>
                  <span title="Save">Sv {u.save || '—'}</span>
                  <span title="Control">Ctrl {u.control || '—'}</span>
                  {u.ward ? <span title="Ward">Wd {u.ward}</span> : null}
                </span>
                <span className="bb-ws-pts">{u.points ? `${u.points} pts` : ''}{u.unit_size ? ` · ${u.unit_size} model${String(u.unit_size) === '1' ? '' : 's'}` : ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// `open` (which warscroll is showing in detail, or null for the army badges)
// lives in BattleBuddyPage so it survives switching to Phases and back.
function ArmyWarscrolls({ friendly, enemy, viewMode, singleSide, open, setOpen }) {
  // open = { side, id } | null
  const lists = { friendly: friendly.units, enemy: enemy.units };
  const sides = viewMode === 'dual' ? ['friendly', 'enemy'] : [singleSide];
  const list = open ? lists[open.side] : [];
  const idx = open ? list.findIndex(u => u.id === open.id) : -1;
  const unit = idx >= 0 ? list[idx] : null;
  const both = friendly.units.length > 0 && enemy.units.length > 0;
  return (
    <div className={`bb-ws-view${viewMode === 'dual' ? ' dual' : ''}`}>
      {sides.map(sd => (
        <ArmyWarscrollsSide key={sd} side={sd} units={lists[sd]} onOpen={(s2, u) => setOpen({ side: s2, id: u.id })} />
      ))}
      {unit && (
        <WarscrollGW
          unit={unit}
          navIndex={idx}
          navList={list}
          sortBy="faction"
          friendlyNavList={both ? friendly.units : undefined}
          enemyNavList={both ? enemy.units : undefined}
          onJumpToUnit={u => setOpen({ side: enemy.units.some(x => x.id === u.id) && !friendly.units.some(x => x.id === u.id) ? 'enemy' : open.side, id: u.id })}
          onClose={() => setOpen(null)}
          onPrev={() => { if (idx > 0) setOpen({ side: open.side, id: list[idx - 1].id }); }}
          onNext={() => { if (idx < list.length - 1) setOpen({ side: open.side, id: list[idx + 1].id }); }}
          onJump={i => list[i] && setOpen({ side: open.side, id: list[i].id })}
        />
      )}
    </div>
  );
}

function FightStage({ friendly, enemy, factionRulesFor, phaseKey, setPhaseKey, commandAbilities, viewMode, setViewMode, contentMode, setContentMode, wsOpen, setWsOpen }) {
  const [singleSide, setSingleSide] = useState('friendly');
  // Ghyran defaults to hidden — matches the previous unconditional behavior
  // (Scourge of Ghyran-named content was always dropped before this checkbox
  // existed). Aqshy is the current season (see the unitSeason/abilitySeason
  // comment above) so it defaults to visible; either can be toggled
  // independently.
  const [hiddenSeasons, setHiddenSeasons] = useState({ ghyran: true, aqshy: false });

  // Page Up/Down flip Friendly <-> Enemy while in Single View — a quick
  // one-handed way to check both sides without reaching for the mouse.
  // Only active in single view (dual already shows both at once); doesn't
  // fire while typing in a field elsewhere on the page.
  useEffect(() => {
    if (viewMode !== 'single') return;
    const onKeyDown = (e) => {
      if (e.key !== 'PageUp' && e.key !== 'PageDown') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      setSingleSide(s => (s === 'friendly' ? 'enemy' : 'friendly'));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewMode]);

  // Up/Down step through the phase ribbon in book order, wrapping past
  // either end — active in both Single and Dual View (unlike PageUp/Down
  // above, which only means something in Single View).
  const advancePhase = useCallback((dir) => {
    // Stepping (▼ button, Up/Down keys, "next phase") cycles Hero → … → End of Turn
    // and back to Hero, skipping Deployment; Deployment is still one click away
    // on the ribbon, and stepping from it goes on to Hero.
    setPhaseKey(k => {
      const cycle = BATTLE_PHASES.filter(p => p.key !== 'deployment');
      if (k === 'deployment') return dir > 0 ? cycle[0].key : cycle[cycle.length - 1].key;
      const idx = cycle.findIndex(p => p.key === k);
      const nextIdx = (idx + dir + cycle.length) % cycle.length;
      return cycle[nextIdx].key;
    });
  }, [setPhaseKey]);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      advancePhase(e.key === 'ArrowDown' ? 1 : -1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [advancePhase]);

  // Voice commands, active only while the Fight step is mounted — same
  // actions as the Up/Down phase hotkeys and the view/side toggle buttons
  // above, just reachable hands-free mid-game.
  useVoiceCommands({
    'move to the next phase': () => advancePhase(1),
    'move to the next': () => advancePhase(1),
    'next phase': () => advancePhase(1),
    'switch to friendly': () => setSingleSide('friendly'),
    'switch to enemy': () => setSingleSide('enemy'),
    'dual view': () => setViewMode('dual'),
    'single view': () => setViewMode('single'),
    'show warscrolls': () => setContentMode('warscrolls'),
    'show phases': () => setContentMode('phases'),
  }, [advancePhase, setSingleSide, setViewMode, setContentMode]);

  const abilityContent = viewMode === 'dual' ? (
    <div className="bb-fight-dual">
      <FightPane side="friendly" state={friendly} factionRulesFor={factionRulesFor} phaseKey={phaseKey} commandAbilities={commandAbilities} hiddenSeasons={hiddenSeasons} />
      <FightPane side="enemy" state={enemy} factionRulesFor={factionRulesFor} phaseKey={phaseKey} commandAbilities={commandAbilities} hiddenSeasons={hiddenSeasons} />
    </div>
  ) : (
    <div className="bb-fight-single">
      <FightPane side={singleSide} state={singleSide === 'friendly' ? friendly : enemy} factionRulesFor={factionRulesFor} phaseKey={phaseKey} commandAbilities={commandAbilities} hiddenSeasons={hiddenSeasons} />
    </div>
  );

  return (
    <div className="bb-fight-wrap">
      <div className="bb-fight-controls">
        <div className="bb-fight-view-toggle">
          <button className={viewMode === 'single' ? 'active' : ''} onClick={() => setViewMode('single')}>Single View</button>
          <button className={viewMode === 'dual' ? 'active' : ''} onClick={() => setViewMode('dual')}>Dual View</button>
        </div>
        <div className="bb-fight-outdated-toggles">
          <span className="bb-fight-outdated-toggles-label">Hide:</span>
          {Object.keys(SEASON_LABELS).map(season => (
            <label className="bb-fight-outdated-toggle" key={season}>
              <input
                type="checkbox"
                checked={hiddenSeasons[season]}
                onChange={e => setHiddenSeasons(s => ({ ...s, [season]: e.target.checked }))}
              />
              {SEASON_LABELS[season]}
            </label>
          ))}
        </div>
      </div>
      {viewMode === 'single' && (
        <div className="bb-fight-side-toggle-row">
          <div className="bb-fight-single-side-toggle">
            <button className={singleSide === 'friendly' ? 'active' : ''} onClick={() => setSingleSide('friendly')}>Friendly</button>
            <button className={singleSide === 'enemy' ? 'active' : ''} onClick={() => setSingleSide('enemy')}>Enemy</button>
          </div>
        </div>
      )}

      {contentMode === 'warscrolls' ? (
        <div className="bb-fight-vertical">
          <div className="bb-fight-main">
            <ArmyWarscrolls friendly={friendly} enemy={enemy} viewMode={viewMode} singleSide={singleSide} open={wsOpen} setOpen={setWsOpen} />
          </div>
        </div>
      ) : (
      <div className="bb-fight-vertical">
        <div className="bb-fight-phase-sidebar">
          <PhaseRibbon phaseKey={phaseKey} setPhaseKey={setPhaseKey} />
          <button
            type="button"
            className="bb-phase-next-btn"
            title="Next Phase (Down arrow)"
            onClick={() => advancePhase(1)}
          >▼</button>
        </div>
        <div className="bb-fight-main">{abilityContent}</div>
      </div>
      )}
    </div>
  );
}

export default function BattleBuddyPage() {
  const [stage, setStage] = useState('select');
  const [friendly, setFriendly] = useState(makeBlankSide());
  const [enemy, setEnemy] = useState(makeBlankSide());
  const factionRules = useFactionRules();
  const commandAbilities = useCommandAbilities();

  // Fight stage's phase selection lives up here (not inside FightStage) so it
  // survives toggling back to Step 1 and returning to Step 2 — only reset to
  // Hero Phase when the actual matchup (which units are on the table for
  // either side) changes, not on every stage switch.
  const [phaseKey, setPhaseKey] = useState('hero');
  // Single View vs Dual View on the Fight step — lives up here (like
  // phaseKey) so it round-trips through the same server-side snapshot
  // instead of resetting to Single View on every refresh/new session.
  const [viewMode, setViewMode] = useState('single');
  // Fight step content: 'phases' (abilities by phase) or 'warscrolls' (the army's
  // warscrolls). Remembered per browser.
  const [contentMode, setContentModeState] = useState(() => {
    try { return localStorage.getItem('bb-content-mode') === 'warscrolls' ? 'warscrolls' : 'phases'; } catch { return 'phases'; }
  });
  const setContentMode = useCallback((m) => {
    setContentModeState(m);
    try { localStorage.setItem('bb-content-mode', m); } catch {}
  }, []);

  // Warscrolls view: the unit open in detail ({ side, id }) or null = army badges.
  // Kept here so Tab / Phases / Warscrolls switching comes back to the same state.
  const [wsOpen, setWsOpen] = useState(null);

  // Tab swaps between Phases and Warscrolls (and opens the Fight step), unless
  // you're typing in a field. Shift+Tab does the same.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Tab' || e.ctrlKey || e.altKey || e.metaKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      e.preventDefault();
      setContentModeState(m => {
        const next = m === 'phases' ? 'warscrolls' : 'phases';
        try { localStorage.setItem('bb-content-mode', next); } catch {}
        return next;
      });
      setStage('fight');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  const matchupSig = `${friendly.units.map(u => u.id).sort((a, b) => a - b).join(',')}||${enemy.units.map(u => u.id).sort((a, b) => a - b).join(',')}`;
  const matchupSigRef = useRef(null);
  useEffect(() => {
    if (matchupSigRef.current !== null && matchupSigRef.current !== matchupSig) {
      setPhaseKey('hero');
    }
    matchupSigRef.current = matchupSig;
  }, [matchupSig]);

  // ── Resume the last matchup on mount, even from a different device — the
  // stage/friendly/enemy/phase snapshot lives server-side (battle_buddy_state
  // table), not localStorage. matchupSigRef is set to the restored sig BEFORE
  // the state update lands so the "reset to Hero on matchup change" effect
  // above doesn't immediately stomp the restored phaseKey.
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/battle-buddy-state').then(async ({ data }) => {
      if (cancelled) return;
      if (data && (data.friendly || data.enemy)) {
        const [f, e] = await Promise.all([restoreSide(data.friendly), restoreSide(data.enemy)]);
        if (cancelled) return;
        if (f.factionSlug) factionRules.fetchFor(f.factionSlug);
        if (e.factionSlug) factionRules.fetchFor(e.factionSlug);
        matchupSigRef.current = `${f.units.map(u => u.id).sort((a, b) => a - b).join(',')}||${e.units.map(u => u.id).sort((a, b) => a - b).join(',')}`;
        setFriendly(f);
        setEnemy(e);
        if (data.phase_key) setPhaseKey(data.phase_key);
        if (data.view_mode === 'single' || data.view_mode === 'dual') setViewMode(data.view_mode);
        if (data.stage === 'fight') setStage('fight');
      }
      setRestored(true);
    }).catch(() => { if (!cancelled) setRestored(true); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Persist whenever the matchup/stage/phase actually changes, debounced —
  // gated on `restored` so the blank initial state can't race the restore
  // fetch above and overwrite what was actually saved.
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!restored) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      axios.put('/api/battle-buddy-state', {
        stage,
        friendly: sideSnapshot(friendly),
        enemy: sideSnapshot(enemy),
        phase_key: phaseKey,
        view_mode: viewMode,
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(saveTimerRef.current);
  }, [restored, stage, friendly, enemy, phaseKey, viewMode]);

  if (!restored) {
    return (
      <div className="table-page bb-page">
        <div className="bb-pane-empty">Loading…</div>
      </div>
    );
  }

  return (
    <div className="table-page bb-page">
      <div className="page-header bb-header">
        <div className="bb-header-left">
          <div className="page-title">
            Battle Buddy
            <span>Age of Sigmar 4th Edition</span>
          </div>
          {/* The two steps: small, at the left. */}
          <div className="bb-steps bb-steps-small">
            <button className={`bb-step-btn bb-step-btn-small${stage === 'select' ? ' active' : ''}`} onClick={() => setStage('select')}><strong>1:</strong> Select Units</button>
            <button className={`bb-step-btn bb-step-btn-small${stage === 'fight' ? ' active' : ''}`} onClick={() => setStage('fight')}><strong>2:</strong> Fight!</button>
          </div>
        </div>
        {/* Main, centred: what the Fight step shows. Picking one also opens the Fight step. */}
        <div className="bb-mode-main" title="Tab swaps between Phases and Warscrolls">
          <button className={`bb-step-btn${stage === 'fight' && contentMode === 'phases' ? ' active' : ''}`} onClick={() => { setContentMode('phases'); setStage('fight'); }} title="Abilities by battle phase">Phases</button>
          <button className={`bb-step-btn${stage === 'fight' && contentMode === 'warscrolls' ? ' active' : ''}`} onClick={() => { setContentMode('warscrolls'); setStage('fight'); }} title="The selected army's warscrolls">Warscrolls</button>
        </div>
        <div className="bb-header-voice"><VoiceControlWidget /></div>
      </div>

      {stage === 'select' ? (
        <div className="bb-dual-view">
          <SourcePane side="friendly" state={friendly} setState={setFriendly} factionRules={factionRules} />
          <SourcePane side="enemy" state={enemy} setState={setEnemy} factionRules={factionRules} />
        </div>
      ) : (
        <FightStage friendly={friendly} enemy={enemy} factionRulesFor={factionRules.get} phaseKey={phaseKey} setPhaseKey={setPhaseKey} commandAbilities={commandAbilities} viewMode={viewMode} setViewMode={setViewMode} contentMode={contentMode} setContentMode={setContentMode} wsOpen={wsOpen} setWsOpen={setWsOpen} />
      )}
    </div>
  );
}
